import { h, Component } from 'preact';
import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import { canUser } from 'utils/roles';
import * as jsonPointer from 'utils/jsonPointer';

import Toggle from 'components/toggle/toggle';
import HelpTooltip from 'components/tooltip/helpTooltip';
import InheritIndicator from 'components/editor/settings/inheritIndicator';
import Warning from '../../icons/warning';

import s from 'components/editor/settings/fieldBase.sss';

export default class SimpleField extends Component {
	s = s;

	constructor(props) {
		super(props);

		const value = this.getValueRaw(props);
		const enabled = !props.toggleable || (value != null && value !== '');
		this.state = {
			id: props.id || props.name || `field-${crypto.randomUUID()}`,
			enabled,
		};

		this.onToggle = this.onToggle.bind(this);
	}

	isDefinedValue(value = this.getValueRaw()) {
		if (value && typeof value === 'object' && !Array.isArray(value)) {
			return Object.values(value).some(v => v !== undefined);
		}
		return value !== undefined;
	}

	getValueRaw(props = this.props) {
		return props.path ? jsonPointer.get(Store.get().data, props.path) : props.value;
	}

	getValue(props = this.props) {
		return this.getValueRaw(props) ?? props.defaultValue;
	}

	get value() {
		return this.getValue();
	}

	getInheritedValue(props = this.props) {
		return this.props.inheritPath && jsonPointer.get(Store.get().data, props.inheritPath);
	}

	get inheritedValue() {
		return this.getInheritedValue() ?? null;
	}

	get canInherit() {
		return this.props.forceInherits || (this.props.inheritPath && !!this.getInheritedValue());
	}

	get isInheriting() {
		return this.props.forceInherits || (this.canInherit && !this.isDefinedValue(this.getValueRaw()));
	}

	get readonly() {
		return this.props.readonly || (this.props.path && !canUser('write'));
	}

	get disabled() {
		return this.props.disabled || (this.props.toggleable && !this.state.enabled);
	}

	get placeholder() {
		return this.inheritedValue ?? this.props.placeholder;
	}

	get labelFor() {
		return this.state.id;
	}

	getHelp() {
		return this.props.help;
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.toggleable) {
			const currentValue = this.getValue();
			const nextValue = this.getValue(nextProps);
			if (currentValue !== nextValue) {
				const enabled = nextValue !== null;
				if (enabled !== this.state.enabled) {
					this.setState({ enabled });
				}
			}
		}
	}

	onToggle(e) {
		const enabled = e.target.checked;
		const value = enabled ? (this.previousValue ?? this.props.defaultValue ?? '') : null;
		if (!enabled) {
			this.previousValue = this.value;
		}
		if (this.props.path) {
			jsonPointer.setDeep(Store.get().data, this.props.path, value);
		}
		this.setState({ enabled });
		this.props.onInput?.({ target: { value } });
	}

	renderToggleableToggle() {
		if (!this.props.toggleable) return;
		return (
			<Toggle
				class={s.toggle}
				dark={this.props.dark}
				checked={this.state.enabled}
				readonly={this.readonly}
				onInput={this.onToggle}
				key="toggle"
			/>
		);
	}

	renderHelp() {
		const help = this.getHelp();
		if (!help) return;
		return (
			<HelpTooltip class={this.s.help} direction="top" align="start" dark={this.props.dark} key="help">
				{help}
			</HelpTooltip>
		);
	}

	renderInheritIndicator() {
		if (!this.canInherit) return;

		return (
			<InheritIndicator
				class={this.s.inheritIndicator}
				dark={this.props.dark}
				forceInherits={this.props.forceInherits}
				group={this.props.inheritGroup}
				hideReset={this.props.disabled || this.readonly}
				path={this.props.path}
				type={this.props.inheritType}
				tooltipInherit={this.props.tooltipInherit}
				tooltipNotInherit={this.props.tooltipNotInherit}
				align="end"
			/>
		);
	}

	renderName() {
		return (
			<div class={this.s.name} key="name">
				{this.renderToggleableToggle()}
				{this.props.label}
				{this.renderHelp()}
				{this.props.note && <span class={this.s.note}>{this.props.note}</span>}
				{this.props.extraContent}
				{this.renderInheritIndicator()}
			</div>
		);
	}

	renderField() {}

	renderError() {
		return <div class={this.s.errorText} key="error">{this.state.error || this.props.error}</div>;
	}

	renderWarning() {
		if (!this.state.warning && !this.props.warning) return;
		return (
			<div class={this.s.warningText} key="warning">
				<Warning />
				<div>{this.state.warning || this.props.warning}</div>
			</div>
		);
	}

	render(props, state) {
		// Without a target a <label> forwards clicks to its first nested control (e.g. the inherit reset button)
		const Wrapper = this.labelFor ? 'label' : 'div';

		return (
			<Wrapper
				for={this.labelFor}
				class={joinClasses(
					this.s.wrapper,
					props.class,
					props.dark && this.s.dark,
					props.showOnHover && this.s.showOnHover,
					props.toggleable && this.s.toggleable,
					props.hideDisabled && this.s.hideDisabled,
					this.explicitDisabled && this.disabled && this.s.disabled,
				)}
			>
				{this.renderName()}
				{this.renderField()}
				{this.renderError()}
				{this.renderWarning()}
			</Wrapper>
		);
	}
}
